<script setup lang="ts">
  import { onMounted, onUnmounted, ref, watch } from 'vue';
  import IconImage from './IconImage.vue';

  export interface ActionMenuItem {
    id: string;
    label: string;
    icon?: string;
    onClick: () => void;
    class?: string;
    disabled?: boolean;
  }

  const props = withDefaults(
    defineProps<{
      items: ActionMenuItem[];
      modelValue?: boolean;
      menuClass?: string;
      /**
       * Replaces the classes of every item. The default is the shape the current
       * consumers already render, so leaving it out changes nothing.
       */
      itemClass?: string;
      stopTriggerClickPropagation?: boolean;
    }>(),
    {
      items: () => [],
      menuClass: 'absolute right-0 mt-2 w-49 bg-white-01 rounded-2xl shadow-2xl z-10',
      itemClass:
        'flex flex-shrink-0 whitespace-nowrap gap-3 w-full text-left px-4 py-2 text-md text-gray-07 font-medium hover:bg-gray-01',
      stopTriggerClickPropagation: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  // The `disabled:` variants outrank the plain text and hover classes of `itemClass`, so a
  // consumer that replaces `itemClass` still gets a gray item that does not light up on hover.
  const DISABLED_ITEM_CLASS =
    'disabled:cursor-not-allowed disabled:text-gray-16 disabled:hover:bg-transparent';

  const isOpen = ref<boolean>(props.modelValue ?? false);
  const menuRef = ref<HTMLDivElement>();
  const triggerRef = ref<HTMLDivElement>();

  const setOpen = (value: boolean) => {
    isOpen.value = value;
    emit('update:modelValue', value);
  };

  const close = () => {
    if (!isOpen.value) return;

    setOpen(false);
  };

  const toggle = () => {
    setOpen(!isOpen.value);
  };

  const handleTriggerClick = (event: MouseEvent) => {
    if (props.stopTriggerClickPropagation) {
      event.stopPropagation();
    }
    toggle();
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    const isClickInside = menuRef.value?.contains(target);
    const isClickOnTrigger = triggerRef.value?.contains(target);

    if (!isClickInside && !isClickOnTrigger) {
      close();
    }
  };

  const handleItemClick = (item: ActionMenuItem) => {
    close();
    item.onClick();
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  // Capture phase: a trigger with stopTriggerClickPropagation stops the bubbling click before it
  // reaches document, so a bubbling listener would leave the other open menus open.
  onMounted(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true);
    document.removeEventListener('keydown', handleEscape);
  });

  watch(
    () => props.modelValue,
    (value) => {
      if (value !== undefined) {
        isOpen.value = value;
      }
    }
  );

  defineExpose({ close, toggle });
</script>

<template>
  <div class="relative inline-flex">
    <div
      ref="triggerRef"
      :data-open="isOpen ? '1' : '0'"
      @click="handleTriggerClick"
    >
      <slot
        v-if="$slots.trigger"
        name="trigger"
        :is-open="isOpen"
      />
      <button
        v-else
        type="button"
        class="action-menu-trigger-default"
        aria-label="Abrir acoes"
      >
        Acoes
      </button>
    </div>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="isOpen"
        ref="menuRef"
        :data-open="isOpen ? '1' : '0'"
        :class="menuClass"
      >
        <div class="py-1">
          <button
            v-for="item in props.items"
            :key="item.id"
            :class="[props.itemClass, item.class, item.disabled ? DISABLED_ITEM_CLASS : undefined]"
            :disabled="item.disabled"
            @click="handleItemClick(item)"
          >
            <IconImage
              v-if="item.icon"
              :src="item.icon"
              :alt="item.label"
              class="shrink-0"
            />
            {{ item.label }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>
